import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';

import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles';

import {
  CmsArticle,
  CmsCollection,
  CmsContent,
  CmsContentType,
  CmsTarget,
  CmsTaxonomy,
  CmsTeaser,
  CmsViewType,
} from '@fcom/core-api';
import { isNotBlank } from '@fcom/core';

import { toLinkData } from '../../services/template/data-mappers';
import { getFontLevel, getHeaderLevel } from '../../services/cms-utils/cms-utils';

interface ArticleWithIconsItem {
  article: CmsArticle;
  headerLevel: number;
  headerStyles: string[];
  links: CmsTarget[];
}

@Component({
  selector: 'cms-articles-with-icons',
  templateUrl: './cms-articles-with-icons.component.html',
  styleUrls: ['./cms-articles-with-icons.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
  host: {
    '[class.col-1-12]': '!isFullWidth()',
  },
})
export class CmsArticlesWithIconsComponent {
  readonly CmsContentType = CmsContentType;
  readonly CmsViewType = CmsViewType;
  readonly DEFAULT_HEADER_LEVEL = 3;
  readonly SvgLibraryIcon = SvgLibraryIcon;

  content = input.required<CmsCollection>();

  private tags = computed<CmsTaxonomy[]>(() => {
    return this.content()?.subjectTaxonomyTags || [];
  });

  title = computed<string>(() => {
    return this.content()?.title || this.content()?.teaserTitle;
  });

  articles = computed<ArticleWithIconsItem[]>(() => {
    return (
      this.content().items.filter((i: CmsContent): boolean => {
        return i.contentType === CmsContentType.CMArticle;
      }) as CmsArticle[]
    ).map((article: CmsArticle): ArticleWithIconsItem => {
      return {
        article,
        headerLevel: this.articleHeaderLevel(article),
        headerStyles: this.articleHeaderExtraClasses(article),
        links: (article.related ?? [])
          .map((related: CmsContent): CmsTarget => {
            return toLinkData({ ...related.target, teaserTitle: related.teaserTitle });
          })
          .filter((relatedLink: CmsTarget): boolean => {
            return isNotBlank(relatedLink.url);
          }),
      };
    });
  });

  link = computed<CmsTeaser>(() => {
    return this.content().items.find((i: CmsContent): boolean => {
      return i.contentType === CmsContentType.CMTeaser;
    }) as CmsTeaser;
  });

  hasGridClass = computed<boolean>(() => {
    return isNotBlank(this.content().gridClass);
  });

  isFullWidth = computed<boolean>(() => {
    return this.content()?.gridClass?.includes('grid-col-12') ?? false;
  });

  headerLevel = computed<number>(() => {
    return getHeaderLevel(this.tags()) ?? this.DEFAULT_HEADER_LEVEL;
  });

  headerFontLevel = computed<string>(() => {
    const fontLevel = getFontLevel(this.tags()) ?? 'font-heading-2 font-headline-small';
    return `ms-clear-t nordic-blue-900-text text-icon-primary ${fontLevel}`;
  });

  private articleHeaderLevel(article: CmsArticle): number {
    return article.headerLevel ?? getHeaderLevel(article.subjectTaxonomyTags) ?? Math.min(this.headerLevel() + 1, 6);
  }

  private articleHeaderExtraClasses(article: CmsArticle): string[] {
    const fontLevel = getFontLevel(article.subjectTaxonomyTags);
    return [
      'article-heading-tag',
      'nordic-blue-900-text',
      'text-icon-primary',
      ...(fontLevel ? [fontLevel] : ['font-heading-5 font-title-small']),
    ];
  }
}
